import { useEffect, useMemo, useRef, useState } from "react";
import { api, idr, pesanGalat } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Layers, Plus, X, Pencil, Trash2, PackagePlus, History, AlertTriangle, Tag,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

/** Paket Bundling (Inventory #28) — resep PAKET JUAL dari beberapa komponen
 * (mis. "Paket Hemat" = 1x HP + 1x Casing + 1x Anti Gores) BESERTA harga
 * paketnya. MODEL 23 Agu 2026 (brief bos poin 8-11): paket = CARA MENJUAL,
 * bukan barang — tidak ada SKU fisik "paket" yang berstok sendiri. Stok
 * komponen berkurang OTOMATIS saat paket dijual lewat POS/nota. Rakit/urai
 * stok fisik (model LAMA) sudah DIMATIKAN backend.
 *
 * Brief owner 22 Sep 2026 (F1): "harga dapat di setting pada resep bundling",
 * tujuannya paket lebih murah daripada beli satuan.
 *  - Harga paket = SATU angka: harga jual produk paketnya di Master Produk
 *    (angka yang dibaca kasir). Diatur dari form ini lewat penulis harga yang
 *    SAMA dengan Bulk Harga — izin `inventory_produk.create`, jadi isian harga
 *    hanya bisa diubah akun yang `can("inventory_produk", "create")`.
 *  - Σ harga satuan, Hemat, dan peringatan TIDAK dihitung di layar ini: tabel
 *    membaca `ringkasan_harga` dari GET definisi (`with_harga`), form membaca
 *    POST `/inventory/bundling/pratinjau-harga`. Satu rumus di backend
 *    (`bundling.py::hitung_ringkasan_harga_paket`).
 *  - Angka modal & peringatan "di bawah modal" hanya dikirim server untuk akun
 *    yang boleh melihat modal (`modal_terlihat`) — layar tidak menebaknya.
 *  - "Buat produk paket baru" langsung dari form: backend memakai jalur tulis
 *    Master Produk (`_validate`/`_upsert_data`), bukan jalur kedua. Dulu produk
 *    paket WAJIB dibuat dulu di Master Produk, dan itu yang membuat fitur ini
 *    tidak pernah terlihat dipakai.
 *
 * KONTRAK BACKEND lain (routers/inventory/bundling.py):
 *  - CRUD resep memakai izin `inventory_produk` (tidak ada module key baru).
 *  - Resep BOLEH memuat komponen ber-IMEI — IMEI dipilih SAAT INPUT NOTA.
 *  - Stok kosong saat resep dibuat/diedit HANYA peringatan (poin 9).
 *  - Update resep TIDAK BOLEH mengganti sku_bundle, dan takaran komponen
 *    terkunci setelah resep dipakai menjual (harga paket tetap boleh diubah).
 */

const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

// Nilai sort untuk kolom harga — dibaca dari ringkasan SERVER, bukan dihitung.
const GETTER_SORT_RESEP = {
  harga_paket: (r) => r.ringkasan_harga?.harga_paket ?? null,
  hemat: (r) => r.ringkasan_harga?.hemat ?? null,
};

export default function Bundling() {
  const { can } = useAuth();
  const bolehLihat = can("inventory_produk", "view");
  const bolehBuat = can("inventory_produk", "create");
  const bolehUbah = can("inventory_produk", "update");
  const bolehHapus = can("inventory_produk", "delete");
  // Kunci SAMA dengan `products.py::pastikan_boleh_ubah_harga_jual` (gerbang
  // Bulk Harga) — mengubah harga paket dari form Ubah Resep (yang cuma butuh
  // `update`) tetap menuntut `create`. Tanpa ini tombol Simpan berujung 403.
  const bolehAturHarga = can("inventory_produk", "create");
  const [resep, setResep] = useState([]);
  const [loadingResep, setLoadingResep] = useState(true);
  // Arsip rakit/urai FISIK (model LAMA) — endpoint dipertahankan backend untuk
  // histori pra-23 Agu 2026; sejak model paket virtual tidak pernah bertambah.
  const [riwayat, setRiwayat] = useState([]);
  const [loadingRiwayat, setLoadingRiwayat] = useState(true);
  // Batch P (18 Sep 2026) — flag dari server (`akses_modal.flag_akses`), TIDAK
  // pernah dihitung ulang di layar (aturan 3 § LAYAR D DESAIN-BRIEF-18-SEP.md).
  const [modalTerlihat, setModalTerlihat] = useState(true);

  const [form, setForm] = useState(null); // modal tambah/edit resep
  const [busyForm, setBusyForm] = useState(false);

  // Cek stok virtual (poin 9/11) — "stok paket" DITURUNKAN dari stok
  // komponen (routers/inventory/bundling.py::stok_virtual_bundle), bukan
  // angka tersendiri. Pratinjau saja; penolakan KERAS terjadi saat dijual.
  const [cekBundleId, setCekBundleId] = useState("");
  const [stokDetail, setStokDetail] = useState(null);
  const [loadingStok, setLoadingStok] = useState(false);
  const resepSort = useSortState(null, "asc");
  const riwayatSort = useSortState(null, "asc");

  const muatResep = async () => {
    if (!bolehLihat) { setLoadingResep(false); return; }
    setLoadingResep(true);
    try {
      const r = await api.get("/inventory/bundling/definisi", { params: { include_inactive: true, with_harga: true } });
      setResep(r.data.items || []);
      setModalTerlihat(!!r.data.modal_terlihat);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memuat resep paket"));
    } finally { setLoadingResep(false); }
  };

  const muatRiwayat = async () => {
    if (!bolehLihat) { setLoadingRiwayat(false); return; }
    setLoadingRiwayat(true);
    try {
      const r = await api.get("/inventory/bundling");
      setRiwayat(r.data.items || []);
      setModalTerlihat(!!r.data.modal_terlihat);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memuat arsip perakitan lama"));
    } finally { setLoadingRiwayat(false); }
  };

  useEffect(() => {
    muatResep();
    muatRiwayat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolehLihat]);

  useEffect(() => {
    if (!cekBundleId) { setStokDetail(null); return; }
    let batal = false;
    setLoadingStok(true);
    (async () => {
      try {
        const r = await api.get(`/inventory/bundling/definisi/${cekBundleId}/stok`);
        if (!batal) setStokDetail(r.data);
      } catch (e) {
        if (!batal) toast.error(pesanGalat(e, "Gagal memuat stok virtual paket"));
      } finally {
        if (!batal) setLoadingStok(false);
      }
    })();
    return () => { batal = true; };
  }, [cekBundleId]);

  const resepUntukCek = useMemo(
    () => resep.find(r => r.id === cekBundleId) || null,
    [resep, cekBundleId],
  );

  // `harga_paket_awal` = harga yang DILIHAT form saat dibuka (0 = kosong,
  // null = tidak diketahui). Daftar resep dimuat bersama halaman, jadi harga
  // yang diisikan ke form bisa sudah basi saat disimpan (Bulk Harga/tab lain
  // mengubahnya di sela). Dulu form selalu mengirim ulang angka itu, dan
  // simpan yang cuma ganti nama pun mengembalikan harga lama diam-diam.
  // Sekarang harga hanya dikirim kalau isiannya DIUBAH dari angka awal ini,
  // dan backend menolak 409 kalau harga di DB sudah bukan angka ini lagi.
  const bukaTambah = () => setForm({
    id: null, nama: "", mode: "ada", sku_bundle: "", nama_produk_jadi: "",
    komponen: [{ sku: "", nama: "", qty: 1 }], is_active: true, harga_paket: "", harga_paket_awal: null,
    produk_baru: { nama: "", sku: "", kategori: "", brand: "" },
  });

  const bukaEdit = (r) => {
    const rk = r.ringkasan_harga;
    setForm({
      id: r.id, nama: r.nama, mode: "ada", sku_bundle: r.sku_bundle, nama_produk_jadi: r.nama_bundle || r.sku_bundle,
      komponen: (r.komponen || []).map(k => ({ sku: k.sku, nama: k.nama || k.sku, qty: k.qty })),
      is_active: !!r.is_active,
      harga_paket: rk?.harga_paket ? String(rk.harga_paket) : "",
      // Ringkasan gagal dimuat (best-effort di backend) = awal tidak diketahui.
      harga_paket_awal: rk ? (Number(rk.harga_paket) || 0) : null,
      produk_baru: { nama: "", sku: "", kategori: "", brand: "" },
    });
  };

  // 409 = harga paket sudah diubah jalur lain sejak form dibuka. Form TETAP
  // terbuka (nama & isi paket yang sedang diedit tidak boleh hilang), tapi
  // isian harga dikembalikan ke harga TERBARU — tidak ada harga yang ditimpa
  // tanpa diketik ulang dengan sadar.
  const muatUlangHargaForm = async (skuBundle) => {
    try {
      const { data } = await api.post("/inventory/bundling/pratinjau-harga", { sku_bundle: skuBundle, komponen: [] });
      const kini = Number(data.harga_paket) || 0;
      setForm(f => (f && f.sku_bundle === skuBundle)
        ? { ...f, harga_paket: kini > 0 ? String(kini) : "", harga_paket_awal: kini }
        : f);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memuat harga paket terbaru — tutup form lalu buka lagi"));
    }
  };

  const simpanResep = async (satuanPaket) => {
    if (!form.nama.trim()) return toast.error("Nama resep wajib diisi");
    const modeBaru = !form.id && form.mode === "baru";
    const pb = form.produk_baru;
    if (modeBaru) {
      if (!pb.nama.trim()) return toast.error("Nama produk paket baru wajib diisi");
      if (!pb.sku.trim()) return toast.error("SKU produk paket baru wajib diisi");
      if (!pb.kategori) return toast.error("Pilih kategori produk paket baru");
      if (!pb.brand.trim()) return toast.error("Brand produk paket baru wajib diisi");
      if (!(Number(form.harga_paket) > 0)) return toast.error("Harga paket wajib diisi (lebih dari Rp 0)");
    } else if (!form.sku_bundle) {
      return toast.error("Pilih produk paket dari daftar pencarian dulu, atau pilih 'Buat produk paket baru'");
    }
    const komponenValid = form.komponen.filter(k => k.sku);
    if (komponenValid.length === 0) return toast.error("Resep butuh minimal 1 komponen");
    if (komponenValid.some(k => !(Number(k.qty) > 0))) return toast.error("Qty tiap komponen harus lebih dari 0");
    const hargaDiisi = String(form.harga_paket ?? "").trim() !== "";
    if (hargaDiisi && !(Number(form.harga_paket) > 0)) return toast.error("Harga paket harus lebih dari Rp 0");
    const hargaAwal = form.harga_paket_awal;
    // Produk paket baru selalu butuh harga; produk yang sudah ada hanya
    // dikirim harganya kalau isian benar-benar diubah dari harga awal form.
    const hargaDiubah = hargaDiisi && (modeBaru || hargaAwal == null || Number(form.harga_paket) !== Number(hargaAwal));
    setBusyForm(true);
    try {
      const payload = {
        sku_bundle: modeBaru ? "" : form.sku_bundle,
        nama: form.nama.trim(),
        komponen: komponenValid.map(k => ({ sku: k.sku, qty: Number(k.qty) || 0 })),
        is_active: !!form.is_active,
      };
      // Harga hanya dikirim oleh akun yang boleh mengubahnya — akun lain
      // menyimpan resep tanpa menyentuh harga (backend: None = tidak diubah).
      if (bolehAturHarga && hargaDiubah) {
        payload.harga_paket = Number(form.harga_paket);
        if (!modeBaru && hargaAwal != null) payload.harga_paket_awal = Number(hargaAwal);
      }
      if (modeBaru) {
        payload.produk_baru = {
          sku: pb.sku.trim().toUpperCase(), nama: pb.nama.trim(), kategori: pb.kategori,
          brand: pb.brand.trim(), satuan: satuanPaket || "pcs", is_active: true,
          has_imei: false, identifier_type: "none",
        };
      }
      let res;
      if (form.id) {
        res = await api.put(`/inventory/bundling/definisi/${form.id}`, payload);
      } else {
        res = await api.post("/inventory/bundling/definisi", payload);
      }
      toast.success(res.data.message || "Resep paket tersimpan");
      // Peringatan stok kosong (poin 9) & peringatan harga (F1) — TIDAK
      // memblokir simpan, ditampilkan terpisah dari toast sukses.
      const peringatan = res.data.peringatan_stok_kosong || [];
      if (peringatan.length > 0) {
        toast.warning(
          `Peringatan: ${peringatan.length} komponen sedang kosong stok (${peringatan.map(p => p.nama || p.sku).join(", ")}) — resep tetap tersimpan.`,
          { duration: 8000 },
        );
      }
      (res.data.ringkasan_harga?.peringatan || []).forEach(p => toast.warning(p.pesan, { duration: 8000 }));
      setForm(null);
      await muatResep();
    } catch (e) {
      // Satu-satunya 409 untuk produk yang sudah ada = harga basi (lihat
      // `muatUlangHargaForm`); 409 mode produk baru adalah tabrakan SKU.
      const hargaBasi = e?.response?.status === 409 && !modeBaru && !!form.sku_bundle;
      toast.error(pesanGalat(e, "Gagal menyimpan resep paket"), hargaBasi ? { duration: 10000 } : undefined);
      if (hargaBasi) {
        await muatUlangHargaForm(form.sku_bundle);
        await muatResep();
      }
    } finally { setBusyForm(false); }
  };

  const nonaktifkanResep = async (r) => {
    if (!window.confirm(
      `Nonaktifkan resep '${r.nama}'?\n\nResep ini tidak akan bisa dipakai menjual paket baru, tapi ` +
      "riwayat penjualan lama yang memakai resep ini tetap utuh."
    )) return;
    try {
      const res = await api.delete(`/inventory/bundling/definisi/${r.id}`);
      toast.success(res.data.message || "Resep paket dinonaktifkan");
      await muatResep();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menonaktifkan resep paket"));
    }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8" data-testid="bundling">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          Akun Anda belum diberi izin melihat Paket Bundling. Minta admin mencentang modul
          "Inventory · Master Produk" pada role Anda.
        </div>
      </div>
    );
  }

  const jumlahKolomResep = 9 + (modalTerlihat ? 1 : 0);

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="bundling">
      <div>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Paket Bundling</h1>
        <p className="text-sm text-slate-600 mt-1">
          Jual beberapa produk sekaligus sebagai satu paket dengan harga lebih murah daripada beli
          satuan. Atur isi paket dan harga paketnya di sini — stok komponen berkurang otomatis saat
          paket dijual di kasir atau Input Nota.
        </p>
      </div>

      {/* ===== (a) Resep Paket ===== */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-slate-500" />
            <h2 className="font-display font-bold text-slate-900 text-sm">Resep &amp; Harga Paket</h2>
          </div>
          {bolehBuat && (
            <button onClick={bukaTambah} data-testid="bd-resep-tambah"
                    className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Paket Baru
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          {loadingResep ? (
            <LoadingState loading data-testid="bd-resep-loading" />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <SortTh label="Produk Paket" sortKeyName="nama_bundle" sortKey={resepSort.sortKey} sortDir={resepSort.sortDir} onSort={resepSort.toggle} testid="bd-resep-sort-produk" />
                  <SortTh label="Nama Resep" sortKeyName="nama" sortKey={resepSort.sortKey} sortDir={resepSort.sortDir} onSort={resepSort.toggle} testid="bd-resep-sort-nama" />
                  <th className="px-3 py-2.5 font-semibold">Isi Paket</th>
                  <SortTh label="Harga Paket" sortKeyName="harga_paket" sortKey={resepSort.sortKey} sortDir={resepSort.sortDir} onSort={resepSort.toggle} align="right" testid="bd-resep-sort-harga" />
                  <th className="px-3 py-2.5 font-semibold text-right">Σ Harga Satuan</th>
                  <SortTh label="Hemat" sortKeyName="hemat" sortKey={resepSort.sortKey} sortDir={resepSort.sortDir} onSort={resepSort.toggle} align="right" testid="bd-resep-sort-hemat" />
                  {/* Batch P: kolom modal hanya untuk akun yang berhak (`modal_terlihat` dari server). */}
                  {modalTerlihat && (
                    <th className="px-3 py-2.5 font-semibold text-right">Perk. Modal Komponen</th>
                  )}
                  <th className="px-3 py-2.5 font-semibold text-right">Stok Paket</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {resep.length === 0 ? (
                  <tr><td colSpan={jumlahKolomResep} className="px-4 py-10 text-center text-slate-400">Belum ada resep paket</td></tr>
                ) : sortRows(resep, resepSort.sortKey, resepSort.sortDir, GETTER_SORT_RESEP).map(r => {
                  const rk = r.ringkasan_harga;
                  return (
                    <tr key={r.id} data-testid={`bd-resep-row-${r.id}`} className="hover:bg-slate-50/60 align-top">
                      <td className="px-3 py-2.5">
                        <p className="text-slate-800">{r.nama_bundle}</p>
                        <p className="font-mono text-[11px] text-slate-500">{r.sku_bundle}</p>
                      </td>
                      <td className="px-3 py-2.5 text-slate-800">{r.nama}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">
                        {(r.komponen || []).map(k => `${k.qty}× ${k.nama || k.sku}`).join(", ")}
                        {(r.peringatan_stok_kosong || []).length > 0 && (
                          <span className="block mt-1 text-[10px] text-amber-700 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {r.peringatan_stok_kosong.length} komponen kosong stok
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap" data-testid={`bd-resep-harga-${r.id}`}>
                        {rk?.harga_paket ? idr(rk.harga_paket) : <span className="text-amber-700 font-sans text-xs">Belum diisi</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap text-slate-600" data-testid={`bd-resep-total-satuan-${r.id}`}>
                        {rk ? idr(rk.total_harga_satuan) : "-"}
                      </td>
                      <td className="px-3 py-2.5 text-right" data-testid={`bd-resep-hemat-${r.id}`}>
                        <span className={`font-mono whitespace-nowrap ${rk?.hemat > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                          {rk?.hemat == null ? "-" : idr(rk.hemat)}
                        </span>
                        {(rk?.peringatan || []).length > 0 && (
                          <div className="mt-1 space-y-0.5 text-left" data-testid={`bd-resep-peringatan-${r.id}`}>
                            {rk.peringatan.map(p => (
                              <p key={p.kode} className="text-[10px] text-amber-700 flex items-start gap-1 max-w-[16rem]">
                                <AlertTriangle className="w-3 h-3 shrink-0 mt-px" /> {p.pesan}
                              </p>
                            ))}
                          </div>
                        )}
                      </td>
                      {modalTerlihat && (
                        <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap text-slate-600" data-testid={`bd-resep-perk-modal-${r.id}`}>
                          {/* modal_paket null + modal_lengkap false = tak satu pun
                              komponen punya modal; BUKAN Rp 0. */}
                          {rk && rk.modal_paket != null ? idr(rk.modal_paket)
                            : rk && rk.modal_lengkap === false ? <span className="font-sans text-xs text-slate-400">belum diketahui</span>
                            : "-"}
                          {rk && rk.modal_paket != null && rk.modal_lengkap === false && (
                            <span className="block text-[10px] font-sans text-slate-400">sebagian belum diketahui</span>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-right font-mono" data-testid={`bd-resep-stok-virtual-${r.id}`}>
                        {r.stok_virtual ? r.stok_virtual.qty : "-"}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${r.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                          {r.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {bolehUbah && (
                            <button onClick={() => bukaEdit(r)} data-testid={`bd-resep-edit-${r.id}`}
                                    title="Ubah resep & harga paket" className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {bolehHapus && r.is_active && (
                            <button onClick={() => nonaktifkanResep(r)} data-testid={`bd-resep-nonaktifkan-${r.id}`}
                                    title="Nonaktifkan resep" className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ===== (b) Cek Stok Paket ===== */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2">
          <PackagePlus className="w-4 h-4 text-slate-500" />
          <h2 className="font-display font-bold text-slate-900 text-sm">Cek Stok Paket</h2>
        </div>
        <p className="text-xs text-slate-500">
          "Stok paket" bukan angka tersendiri — dihitung langsung dari stok komponen yang tersedia
          sekarang (MIN dari stok tiap komponen ÷ kebutuhan resep), dijumlah dari semua gudang. Stok
          komponen berkurang otomatis setiap kali paket ini terjual.
        </p>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Resep</label>
          <select value={cekBundleId} data-testid="bd-cekstok-resep"
                  onChange={e => setCekBundleId(e.target.value)} className={inp}>
            <option value="">— pilih resep —</option>
            {resep.map(r => (
              <option key={r.id} value={r.id}>
                {r.nama} ({r.sku_bundle}){!r.is_active ? " — nonaktif" : ""}
              </option>
            ))}
          </select>
        </div>
        {loadingStok ? (
          <LoadingState loading data-testid="bd-cekstok-loading" />
        ) : stokDetail && resepUntukCek ? (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm" data-testid="bd-cekstok-hasil">
            <p className="font-semibold text-slate-800">
              Stok paket tersedia: <span className="font-mono">{stokDetail.qty}</span>
              {stokDetail.pembatas && (
                <span className="text-xs text-slate-500 font-normal"> (dibatasi komponen {stokDetail.pembatas})</span>
              )}
            </p>
            <table className="w-full text-xs mt-2">
              <thead className="text-slate-500">
                <tr className="text-left">
                  <th className="py-1 pr-2 font-medium">Komponen</th>
                  <th className="py-1 pr-2 font-medium text-right">Qty/Paket</th>
                  <th className="py-1 pr-2 font-medium text-right">Stok Komponen</th>
                  <th className="py-1 font-medium text-right">Cukup untuk Berapa Paket</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {(stokDetail.rincian || []).map(r => (
                  <tr key={r.sku}>
                    <td className="py-1 pr-2">{r.nama || r.sku}</td>
                    <td className="py-1 pr-2 text-right font-mono">{r.qty_resep}</td>
                    <td className="py-1 pr-2 text-right font-mono">{r.stok_komponen}</td>
                    <td className="py-1 text-right font-mono">{r.tersedia_paket}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">Pilih resep untuk melihat rincian stok komponennya.</p>
        )}
      </div>

      {/* ===== (c) Arsip perakitan stok lama (model pra-23 Agu 2026) =====
          Hanya TERLIHAT kalau memang ada isinya — di produksi nol baris sejak
          model paket virtual, jadi seksi kosong ini dulu cuma membingungkan.
          Wadahnya (data-testid `bd-riwayat`) tetap ada di DOM, disembunyikan
          lewat `hidden`, supaya testid lama tidak lenyap saat arsip kosong. */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="bd-riwayat"
           hidden={!loadingRiwayat && riwayat.length === 0}>
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <h2 className="font-display font-bold text-slate-900 text-sm">Arsip Perakitan Stok Lama</h2>
        </div>
        <p className="px-4 pt-2 text-xs text-slate-500">
          Catatan perakitan stok fisik sebelum 23 Agu 2026 — cara itu sudah digantikan paket jual
          di atas, jadi tabel ini tidak akan bertambah lagi.
        </p>
        <div className="overflow-x-auto">
          {loadingRiwayat ? (
            <LoadingState loading data-testid="bd-riwayat-loading" />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} testid="bd-riwayat-sort-tanggal" />
                  <SortTh label="Resep" sortKeyName="nama_bundle" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} testid="bd-riwayat-sort-resep" />
                  <SortTh label="Qty" sortKeyName="qty" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} align="right" testid="bd-riwayat-sort-qty" />
                  <th className="px-3 py-2.5 font-semibold">Arah</th>
                  {/* Batch P (18 Sep 2026): kolom & sortir modal hanya untuk
                      akun yang berhak (`modal_terlihat` dari server). */}
                  {modalTerlihat && (
                    <SortTh label="Modal Total" sortKeyName="total_modal" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} align="right" testid="bd-riwayat-sort-modal" />
                  )}
                  <th className="px-3 py-2.5 font-semibold">Gudang</th>
                  <th className="px-3 py-2.5 font-semibold">Pembuat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortRows(riwayat, riwayatSort.sortKey, riwayatSort.sortDir).map(row => (
                  <tr key={row.id} data-testid={`bd-riwayat-row-${row.id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2.5">
                      <p className="font-mono text-[11px] text-slate-500">{row.code}</p>
                      <p className="text-slate-800">{row.tanggal}</p>
                    </td>
                    <td className="px-3 py-2.5 text-slate-800">
                      {row.nama_bundle}
                      <span className="block font-mono text-[11px] text-slate-400">{row.sku_bundle}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-slate-700">{row.qty}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[11px] px-2 py-0.5 rounded border ${row.tipe === "rakit" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {row.tipe === "rakit" ? "Rakit" : "Urai"}
                      </span>
                    </td>
                    {modalTerlihat && (
                      <td className="px-3 py-2.5 text-right font-mono text-slate-900">{idr(row.total_modal)}</td>
                    )}
                    <td className="px-3 py-2.5 text-slate-600">{row.warehouse_nama}</td>
                    <td className="px-3 py-2.5 text-slate-600">{row.created_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {form && (
        <FormResep form={form} setForm={setForm} busy={busyForm} onSimpan={simpanResep}
                   mengedit={!!form.id} bolehAturHarga={bolehAturHarga} />
      )}
    </div>
  );
}

/** Autocomplete satu baris komponen resep — pola sama dengan `BarisItem` di
 * InventoryPO.jsx / `ItemNamaField` di Sales.jsx. Barang ber-IMEI BOLEH
 * dipilih (poin 8, brief bos 23 Agu 2026) — IMEI unit fisik yang benar-benar
 * dipakai dipilih staf SAAT INPUT NOTA, bukan di sini. */
function KomponenRow({ idx, item, onUbah, onHapus, bisaHapus }) {
  const [saran, setSaran] = useState([]);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cari = (teks) => {
    onUbah(idx, { nama: teks, sku: "" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 2) { setSaran([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      try {
        const { data } = await api.get("/inventory/products/lookup", { params: { q } });
        if (seq === reqSeq.current) setSaran(data.items || []);
      } catch { if (seq === reqSeq.current) setSaran([]); }
    }, 300);
  };

  const pilih = (p) => {
    onUbah(idx, { sku: p.sku, nama: p.nama });
    setSaran([]);
  };

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0 relative">
        <input value={item.nama} placeholder="Ketik SKU atau nama komponen" onChange={e => cari(e.target.value)}
               data-testid={`bd-komponen-sku-${idx}`} className={inp} />
        {item.sku && (
          <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
            SKU {item.sku}
          </span>
        )}
        {saran.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
            {saran.map(p => {
              const berImei = !!p.has_imei;
              return (
                <button key={p.sku} type="button"
                        onClick={() => pilih(p)}
                        title={berImei ? "Ber-IMEI — IMEI unit dipilih saat input nota, bukan di sini" : ""}
                        data-testid={`bd-komponen-saran-${idx}-${p.sku}`}
                        className="w-full text-left px-3 py-2 text-xs border-b border-slate-50 last:border-0 hover:bg-emerald-50">
                  <span className="font-medium text-slate-900">{p.nama}</span>
                  <span className="font-mono text-[10px] text-slate-400 ml-1.5">{p.sku}</span>
                  {berImei && <span className="text-[10px] text-sky-700 ml-1.5">ber-IMEI</span>}
                  <span className="text-[10px] text-slate-500 ml-1.5">{idr(p.harga_jual)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* `inp` membawa `w-full`, dan di CSS hasil build `w-full` menang atas
          `w-24` — kolom qty dulu melebar dan kolom nama komponen tergencet
          sampai tak terbaca (terlihat saat verifikasi layar 23 Sep 2026). */}
      <input type="number" min="1" value={item.qty} placeholder="Qty"
             onChange={e => onUbah(idx, { qty: e.target.value })}
             data-testid={`bd-komponen-qty-${idx}`} className={`${inp.replace("w-full ", "")} w-24 shrink-0`} />
      {bisaHapus && (
        <button type="button" onClick={() => onHapus(idx)} data-testid={`bd-komponen-hapus-${idx}`}
                className="w-10 h-10 rounded hover:bg-rose-50 text-rose-600 grid place-items-center shrink-0">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/** Ringkasan harga paket di dalam form — isinya jawaban server apa adanya
 * (`POST /inventory/bundling/pratinjau-harga`); tidak ada rumus di sini. */
function RingkasanHargaForm({ rk, loading }) {
  if (loading && !rk) return <LoadingState loading data-testid="bd-resep-ringkasan-loading" />;
  if (!rk) return null;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-2" data-testid="bd-resep-ringkasan">
      <table className="w-full text-xs">
        <thead className="text-slate-500">
          <tr className="text-left">
            <th className="py-1 pr-2 font-medium">Komponen</th>
            <th className="py-1 pr-2 font-medium text-right">Qty</th>
            <th className="py-1 pr-2 font-medium text-right">Harga Satuan</th>
            <th className="py-1 font-medium text-right">Subtotal</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {(rk.rincian || []).map(b => (
            <tr key={b.sku}>
              <td className="py-1 pr-2 font-mono text-[11px]">{b.sku}</td>
              <td className="py-1 pr-2 text-right font-mono">{b.qty}</td>
              <td className="py-1 pr-2 text-right font-mono">{b.harga_jual > 0 ? idr(b.harga_jual) : "belum ada"}</td>
              <td className="py-1 text-right font-mono">{idr(b.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <span className="text-slate-600">Σ harga satuan</span>
        <span className="text-right font-mono" data-testid="bd-resep-ringkasan-total">{idr(rk.total_harga_satuan)}</span>
        <span className="text-slate-600">Harga paket</span>
        <span className="text-right font-mono">{rk.harga_paket ? idr(rk.harga_paket) : "-"}</span>
        <span className="text-slate-800 font-semibold">Hemat untuk pembeli</span>
        <span className={`text-right font-mono font-semibold ${rk.hemat > 0 ? "text-emerald-700" : "text-rose-700"}`}
              data-testid="bd-resep-ringkasan-hemat">
          {rk.hemat == null ? "-" : idr(rk.hemat)}
        </span>
        {/* Baris modal HANYA kalau server bilang akun ini boleh melihatnya. */}
        {rk.modal_terlihat && (rk.modal_paket != null || rk.modal_lengkap === false) && (
          <>
            <span className="text-slate-600">Perkiraan modal komponen</span>
            <span className="text-right font-mono" data-testid="bd-resep-ringkasan-modal">
              {rk.modal_paket != null
                ? `${idr(rk.modal_paket)}${rk.modal_lengkap === false ? " (sebagian belum diketahui)" : ""}`
                : "belum diketahui"}
            </span>
          </>
        )}
      </div>
      {(rk.peringatan || []).map(p => (
        <p key={p.kode} data-testid={`bd-resep-ringkasan-peringatan-${p.kode}`}
           className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> {p.pesan}
        </p>
      ))}
    </div>
  );
}

function FormResep({ form, setForm, busy, onSimpan, mengedit, bolehAturHarga }) {
  const [saranBundle, setSaranBundle] = useState([]);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);
  const [ringkasan, setRingkasan] = useState(null);
  const [loadingRingkasan, setLoadingRingkasan] = useState(false);
  // Opsi kategori/brand/satuan untuk "produk paket baru" — dari registry
  // yang SAMA dengan Master Produk (`GET /inventory/master-opsi`); dimuat
  // hanya saat mode itu dipilih.
  const [opsi, setOpsi] = useState(null);
  const modeBaru = !mengedit && form.mode === "baru";

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  useEffect(() => {
    if (!modeBaru || opsi) return;
    let batal = false;
    (async () => {
      try {
        const { data } = await api.get("/inventory/master-opsi");
        if (!batal) setOpsi(data);
      } catch (e) {
        if (!batal) toast.error(pesanGalat(e, "Gagal memuat pilihan kategori/brand"));
      }
    })();
    return () => { batal = true; };
  }, [modeBaru, opsi]);

  // Kategori non-gadget saja: kategori gadget (Handphone/Tab/dst.) membentuk
  // Nama & SKU otomatis dari Type/Storage/Warna — tidak cocok untuk produk
  // paket, dan form itu hanya ada di Master Produk.
  const kategoriPaket = useMemo(
    () => (opsi?.kategori_utama || []).filter(k => !k.wajib_status),
    [opsi],
  );
  // Saran brand gabungan; backend yang menegakkan daftar per kategori.
  const saranBrand = useMemo(() => {
    const semua = [...(opsi?.brand_accessories || []), ...(opsi?.brand_gadget || []), ...(opsi?.brand || [])];
    return [...new Set(semua)];
  }, [opsi]);
  const satuanPaket = useMemo(() => {
    const daftar = opsi?.satuan || [];
    if (!daftar.length || daftar.some(s => String(s).toLowerCase() === "pcs")) return "pcs";
    return daftar[0];
  }, [opsi]);

  // Kunci pratinjau — string supaya efek di bawah hanya berjalan saat isi
  // yang relevan benar-benar berubah (bukan tiap render).
  const kunciPratinjau = useMemo(() => JSON.stringify({
    sku_bundle: modeBaru ? "" : (form.sku_bundle || ""),
    komponen: form.komponen.filter(k => k.sku).map(k => ({ sku: k.sku, qty: Number(k.qty) || 0 })),
    harga_paket: String(form.harga_paket ?? "").trim() === "" ? null : Number(form.harga_paket),
  }), [modeBaru, form.sku_bundle, form.komponen, form.harga_paket]);

  useEffect(() => {
    const isi = JSON.parse(kunciPratinjau);
    if (isi.komponen.length === 0) { setRingkasan(null); return; }
    let batal = false;
    setLoadingRingkasan(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/inventory/bundling/pratinjau-harga", isi);
        if (!batal) setRingkasan(data);
      } catch {
        if (!batal) setRingkasan(null);
      } finally {
        if (!batal) setLoadingRingkasan(false);
      }
    }, 400);
    return () => { batal = true; clearTimeout(t); };
  }, [kunciPratinjau]);

  const cariBundle = (teks) => {
    setForm(f => ({ ...f, nama_produk_jadi: teks, sku_bundle: "" }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 2) { setSaranBundle([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      try {
        const { data } = await api.get("/inventory/products/lookup", { params: { q } });
        if (seq === reqSeq.current) setSaranBundle(data.items || []);
      } catch { if (seq === reqSeq.current) setSaranBundle([]); }
    }, 300);
  };

  // Memilih produk paket mengisi Harga Paket dengan harga jualnya SEKARANG
  // (angka yang dibaca kasir) — mengubahnya di sini berarti mengubah harga itu.
  const pilihBundle = (p) => {
    const hj = Number(p.harga_jual) > 0 ? Number(p.harga_jual) : 0;
    setForm(f => ({
      ...f, sku_bundle: p.sku, nama_produk_jadi: p.nama,
      harga_paket: hj > 0 ? String(hj) : "", harga_paket_awal: hj,
    }));
    setSaranBundle([]);
  };

  const ubahProdukBaru = (patch) => setForm(f => ({ ...f, produk_baru: { ...f.produk_baru, ...patch } }));
  const ubahKomponen = (idx, patch) => setForm(f => ({
    ...f, komponen: f.komponen.map((k, i) => i === idx ? { ...k, ...patch } : k),
  }));
  const hapusKomponen = (idx) => setForm(f => ({ ...f, komponen: f.komponen.filter((_, i) => i !== idx) }));
  const tambahKomponen = () => setForm(f => ({ ...f, komponen: [...f.komponen, { sku: "", nama: "", qty: 1 }] }));

  // Resep BARU hanya bisa dibuat akun `create` (tombol Paket Baru), jadi di
  // sana isian harga selalu terbuka; di form Ubah ia terkunci untuk akun yang
  // cuma punya `update` — persis gerbang backend `_rencana_ubah_harga`.
  const hargaBisaDiubah = bolehAturHarga;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setForm(null)}>
      <div onClick={e => e.stopPropagation()} data-testid="bd-resep-modal"
           className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Layers className="w-5 h-5 text-emerald-600" /> {mengedit ? "Ubah Paket" : "Paket Baru"}
          </h3>
          <button onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
            Harga paket adalah harga jual produk paket yang dibaca kasir. Komponen BOLEH ber-IMEI —
            IMEI unit dipilih saat input nota, bukan di sini. Stok kosong pada komponen HANYA
            peringatan, tidak menghalangi resep ini disimpan.
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nama resep</label>
            <input value={form.nama} onChange={e => setForm(f => ({ ...f, nama: e.target.value }))}
                   placeholder="mis. Paket Hemat A" data-testid="bd-resep-nama" className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Produk paket (yang dipilih kasir)</label>
            {mengedit ? (
              <div className={`${inp} flex items-center bg-slate-50 text-slate-600`}>
                {form.nama_produk_jadi}
                <span className="font-mono text-xs text-slate-400 ml-1.5">({form.sku_bundle})</span>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-2">
                  <button type="button" data-testid="bd-resep-mode-ada"
                          onClick={() => setForm(f => ({ ...f, mode: "ada" }))}
                          className={`h-8 px-3 rounded-lg text-xs border ${form.mode === "ada" ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200 text-slate-600"}`}>
                    Pilih produk yang sudah ada
                  </button>
                  <button type="button" data-testid="bd-resep-mode-baru"
                          onClick={() => setForm(f => ({ ...f, mode: "baru", sku_bundle: "", nama_produk_jadi: "" }))}
                          className={`h-8 px-3 rounded-lg text-xs border ${form.mode === "baru" ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200 text-slate-600"}`}>
                    Buat produk paket baru
                  </button>
                </div>
                {modeBaru ? (
                  <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="bd-produk-baru">
                    <p className="text-[11px] text-slate-500">
                      Produk ini dibuat di Master Produk sekalian saat resep disimpan. Pendapatan paket
                      tercatat di kategori yang dipilih di sini.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input value={form.produk_baru.nama} onChange={e => ubahProdukBaru({ nama: e.target.value })}
                             placeholder="Nama produk paket" data-testid="bd-produk-baru-nama" className={inp} />
                      <input value={form.produk_baru.sku} onChange={e => ubahProdukBaru({ sku: e.target.value.toUpperCase() })}
                             placeholder="SKU produk paket" data-testid="bd-produk-baru-sku" className={`${inp} font-mono`} />
                      <select value={form.produk_baru.kategori} onChange={e => ubahProdukBaru({ kategori: e.target.value })}
                              data-testid="bd-produk-baru-kategori" className={inp}>
                        <option value="">— kategori —</option>
                        {kategoriPaket.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                      </select>
                      <input value={form.produk_baru.brand} onChange={e => ubahProdukBaru({ brand: e.target.value })}
                             list="bd-brand-opsi" placeholder="Brand" data-testid="bd-produk-baru-brand" className={inp} />
                      <datalist id="bd-brand-opsi">
                        {saranBrand.map(b => <option key={b} value={b} />)}
                      </datalist>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      Paket berkategori Handphone/Tab/Laptop/Wearable dibuat dari Master Produk (Nama &amp; SKU
                      gadget dibentuk otomatis di sana), lalu dipilih lewat "Pilih produk yang sudah ada".
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    <input value={form.nama_produk_jadi} onChange={e => cariBundle(e.target.value)}
                           placeholder="Ketik SKU atau nama produk paket" data-testid="bd-resep-sku" className={inp} />
                    {form.sku_bundle && (
                      <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                        SKU {form.sku_bundle}
                      </span>
                    )}
                    {saranBundle.length > 0 && (
                      <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                        {saranBundle.map(p => {
                          const berImei = !!p.has_imei;
                          return (
                            <button key={p.sku} type="button"
                                    onClick={() => pilihBundle(p)}
                                    title={berImei ? "Ber-IMEI — IMEI unit dipilih saat input nota, bukan di sini" : ""}
                                    data-testid={`bd-resep-saran-${p.sku}`}
                                    className="w-full text-left px-3 py-2 text-xs border-b border-slate-50 last:border-0 hover:bg-emerald-50">
                              <span className="font-medium text-slate-900">{p.nama}</span>
                              <span className="font-mono text-[10px] text-slate-400 ml-1.5">{p.sku}</span>
                              {berImei && <span className="text-[10px] text-sky-700 ml-1.5">ber-IMEI</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-600">Isi paket (minimal 1 komponen)</p>
            {form.komponen.map((k, i) => (
              <KomponenRow key={i} idx={i} item={k} onUbah={ubahKomponen} onHapus={hapusKomponen}
                           bisaHapus={form.komponen.length > 1} />
            ))}
            <button type="button" onClick={tambahKomponen} data-testid="bd-komponen-tambah"
                    className="text-sm text-emerald-700 hover:underline">+ Tambah komponen</button>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
              <Tag className="w-3.5 h-3.5" /> Harga paket
            </label>
            {hargaBisaDiubah ? (
              <input type="number" min="1" value={form.harga_paket}
                     onChange={e => setForm(f => ({ ...f, harga_paket: e.target.value }))}
                     placeholder="mis. 1250000" data-testid="bd-resep-harga-paket" className={`${inp} font-mono`} />
            ) : (
              <div className={`${inp} flex items-center bg-slate-50 text-slate-600 font-mono`} data-testid="bd-resep-harga-terkunci">
                {form.harga_paket ? idr(Number(form.harga_paket)) : "Belum diisi"}
              </div>
            )}
            {!hargaBisaDiubah && (
              <p className="text-[11px] text-slate-500 mt-1">
                Mengubah harga paket butuh izin tambah (create) Master Produk — sama dengan Ubah Harga Massal.
              </p>
            )}
          </div>

          <RingkasanHargaForm rk={ringkasan} loading={loadingRingkasan} />

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active} data-testid="bd-resep-aktif"
                   onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                   className="w-4 h-4 rounded border-slate-300" />
            Resep aktif (paket bisa dijual di kasir &amp; Input Nota)
          </label>
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={() => onSimpan(satuanPaket)} disabled={busy} data-testid="bd-resep-modal-submit"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Menyimpan..." : "Simpan Paket"}
          </button>
        </div>
      </div>
    </div>
  );
}
